import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, firstValueFrom, pairwise, Subject, takeUntil } from 'rxjs';
import { EsimService } from '../esim.service';
import { GlobalDataService } from '../../../services/global-data.service';

interface EsimUser {
  esim_status: string;
  country: string;
  details: any;
  pkg_details: any;
  validity: any;
  package_id: any;
  usage_data?: any;
  createdAt?: string;
  activated_at?: string;
  expired_at?: string;
  finished_at?: string;
  recycled_at?: string;
}

@Component({
  selector: 'app-my-esims',
  templateUrl: './my-esims.component.html',
  styleUrls: ['../esim.component.scss']
})
export class MyEsimsComponent implements OnInit, OnDestroy {

  public isLoadingUserEsims = true;
  userEsims: any[] = [];
  private destroy$ = new Subject<void>();

  selectedTabIndex = 0;
  private hasTabParam = false;
  activeEsims: EsimUser[] = [];
  pendingEsims: EsimUser[] = [];
  otherEsims: EsimUser[] = [];
  activeCount = 0;
  pendingCount = 0;
  otherCount = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private esimService: EsimService,
    private translateService: TranslateService,
    private globalDataService: GlobalDataService
  ) {}

  async ngOnInit() {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab !== null) {
      this.selectedTabIndex = parseInt(tab, 10);
      this.hasTabParam = true;
    }

    const cached = this.globalDataService.getUserEsims();
    if (cached) {
      this.userEsims = cached;
      this.parseEsims();
      this.isLoadingUserEsims = false;
    } else if (this.globalDataService.isUserEsimsLoading()) {
      this.isLoadingUserEsims = true;
      await firstValueFrom(
        this.globalDataService.userEsimsLoading$.pipe(filter(loading => !loading))
      );
      const loaded = this.globalDataService.getUserEsims();
      if (loaded !== null) {
        this.userEsims = loaded;
        this.parseEsims();
        this.isLoadingUserEsims = false;
      } else {
        await this.getUserEsimList();
      }
    } else {
      await this.getUserEsimList();
    }

    // Cuando AppComponent refresca los datos en background, actualizar la vista
    this.globalDataService.userEsimsLoading$.pipe(
      distinctUntilChanged(),
      pairwise(),
      filter(([prev, curr]) => prev === true && curr === false),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      const fresh = this.globalDataService.getUserEsims();
      if (fresh) {
        this.userEsims = fresh;
        this.parseEsims();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async getUserEsimList(): Promise<void> {
    this.isLoadingUserEsims = true;
    try {
      this.userEsims = await this.esimService.getEsimList();
      this.globalDataService.setUserEsims(this.userEsims ?? []);
      this.parseEsims();
    } catch (error) {
      console.error('Error fetching user eSIMs:', error);
      this.activeEsims = [];
      this.pendingEsims = [];
      this.otherEsims = [];
      this.updateTabCounts();
    } finally {
      this.isLoadingUserEsims = false;
    }
  }

  private parseEsims(): void {
    if (!this.userEsims || this.userEsims.length === 0) {
      this.activeEsims = [];
      this.pendingEsims = [];
      this.otherEsims = [];
      this.updateTabCounts();
      return;
    }
    this.activeEsims = this.userEsims.filter(e => e.esim_status === 'ACTIVE');
    this.pendingEsims = this.userEsims.filter(e => e.esim_status === 'NOT_ACTIVE');
    this.otherEsims = this.userEsims.filter(e =>
      e.esim_status !== 'ACTIVE' && e.esim_status !== 'NOT_ACTIVE'
    );
    this.updateTabCounts();
    this.globalDataService.userEsimsCount = this.userEsims.length;
  }

  private updateTabCounts(): void {
    this.activeCount = this.activeEsims.length;
    this.pendingCount = this.pendingEsims.length;
    this.otherCount = this.otherEsims.length;
    if (!this.hasTabParam) {
      this.selectedTabIndex = this.activeCount > 0 ? 0
        : this.pendingCount > 0 ? 1
        : 2;
    }
  }

  goToEsimSearch(): void {
    this.router.navigate(['/esim']);
  }

  openTopupsDialog(iccid: string): void {
    const esimDetails = this.userEsims.filter(e => e.iccid == iccid);
    sessionStorage.setItem('esimDetails', JSON.stringify(esimDetails));
    this.router.navigate(['/topup', iccid]);
  }

  openEsimDetailsPage(esimData: any): void {
    sessionStorage.setItem('userEsimDetails', JSON.stringify(esimData));
    this.router.navigate(['/user-esim-details', esimData.details.iccid], {
      state: { esimData },
      queryParams: { tab: this.selectedTabIndex }
    });
  }

  getDaysUntilRecycling(esim: EsimUser): number | null {
    if (!esim.createdAt || esim.esim_status !== 'NOT_ACTIVE') return null;
    const created = new Date(esim.createdAt);
    const deadline = new Date(created);
    deadline.setDate(deadline.getDate() + 90);
    const now = new Date();
    return Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }

  getStatusChipColor(status: string): string {
    switch (status) {
      case 'ACTIVE': return 'status-chip-green';
      case 'NOT_ACTIVE': return 'status-chip-orange';
      case 'EXPIRED':
      case 'FINISHED':
      case 'RECYCLED': return 'status-chip-red';
      default: return 'status-chip-gray';
    }
  }

  getStatusLabel(status: string): string {
    return `ESIM.${status}`;
  }

  getStatusDate(esim: EsimUser): string | null {
    switch (esim.esim_status) {
      case 'ACTIVE': return esim.expired_at || null;
      case 'NOT_ACTIVE': return esim.createdAt || null;
      case 'EXPIRED': return esim.expired_at || null;
      case 'FINISHED': return esim.finished_at || null;
      case 'RECYCLED': return esim.recycled_at || null;
      default: return null;
    }
  }

  getStatusDateLabel(status: string): string {
    switch (status) {
      case 'ACTIVE': return 'ESIM.STATUS.EXPIRES_ON';
      case 'NOT_ACTIVE': return 'ESIM.STATUS.CREATED_ON';
      case 'EXPIRED': return 'ESIM.STATUS.EXPIRED_ON';
      case 'FINISHED': return 'ESIM.STATUS.FINISHED_ON';
      case 'RECYCLED': return 'ESIM.STATUS.RECYCLED_ON';
      default: return 'ESIM.STATUS.DATE';
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(this.translateService.currentLang || 'es', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  calculatePercentage(total: number | undefined, remaining: number | undefined): number {
    if (!total || !remaining) return 0;
    return ((total - remaining) / total) * 100;
  }
}
